// PriceFormClean.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "../styles/price-workspace.css";
import "../styles/shop2000-modern.css";
import "../styles/history-ui.css";
import "../styles/price-dense-overrides.css";


import Shop2000PricesSection from "./Shop2000PricesSection";


import { can } from "../auth/can";
import { PERMS } from "../auth/permissions";

import ConfirmChangesModal from "./ConfirmChangesModal";
import ConfirmNoChangeModal from "./ConfirmNoChangeModal";

import PlannedPricesSection, { transformPlannedPricesData } from "./PlannedPricesSection";

import PriceTableHeader from "./PriceTableHeader";
import StationRow from "./StationRow";
import PriceSnapshotDrawer from "./PriceSnapshotDrawer";

import {
  normalizeStationPrices,
  stationHasEUR,
  getLimitRange,
  clearDirtyForStation,
  buildSubmitRowsForStation,
  fieldKey,
} from "../utils/priceFormUtils";

import {
  buildConfirmItemsForStation,
  buildUnconfirmedItemsForStation,
  enrichLimits,
  buildOutOfLimitNearbyItems,
} from "../utils/priceFormConfirmUtils";

import { usePriceEditing } from "../utils/usePriceEditing";

import {
  submitPrices,
  submitPricesAdmin,
  submitPricesUser,
  submitPricesAdminWithoutChange,
  submitSchedulePriceChange,
  displayHistory,
  getHistoryChangesSummary,
  displayPlannedPrices,
  displayShop2000Prices,
} from "../api_controll/apiAdmin";

import "../styles/history-ui.css";

export default function PriceFormClean({ detail, onPricesSaved }) {
  const user = useSelector((s) => s.user.user);

  const { station: armexStationRaw, nearbyStations: nearbyRaw, meta } = detail || {};
  const layout = meta?.layout || {};
  const allowedMap = meta?.allowed_by_station || {};

  const competitorChecks = meta?.competitor_station_checks || {};
  const latestCompetitorCheck = competitorChecks?.latest || null;
  const lastCompetitorChecks = competitorChecks?.last_5 || [];

  const layoutFuels = layout?.fuels || [];
  const layoutCurrencies = layout?.currencies || ["CZK", "EUR"];

  const [showCheckHistory, setShowCheckHistory] = useState(false);

  const canArmexEdit = useMemo(() => can(user, PERMS.ARMEX_EDIT), [user]);
  const canArmexConfirm = useMemo(() => can(user, PERMS.ARMEX_CONFIRM), [user]);
  const canArmexSchedule = useMemo(() => can(user, PERMS.ARMEX_SCHEDULE), [user]);
  const canNearbyEdit = useMemo(() => can(user, PERMS.NEARBY_EDIT), [user]);
  const canUserConfirm = useMemo(() => can(user, PERMS.USER_CONFIRM_CHANGES), [user]);
  const canSnapshot = useMemo(() => can(user, PERMS.ARMEX_SNAPSHOT), [user]);
  const canPlannedView   = useMemo(() => can(user, PERMS.PLANNED_VIEW), [user]);
  const canPlannedDelete = useMemo(() => can(user, PERMS.PLANNED_DELETE), [user]);

  const [armexData, setArmexData] = useState(null);
  const [stationData, setStationData] = useState([]);

  const [scheduledTime, setScheduledTime] = useState("");
  const [recentlyConfirmed, setRecentlyConfirmed] = useState(false);

  const [snapOpen, setSnapOpen] = useState(false);
  const [snapAt, setSnapAt] = useState("");
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapCompareNow, setSnapCompareNow] = useState(true);
  const [snapshot, setSnapshot] = useState(null);

  const [historySummary, setHistorySummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // MODALS
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmSubtitle, setConfirmSubtitle] = useState("");
  const [confirmItems, setConfirmItems] = useState([]);
  const [confirmWarnings, setConfirmWarnings] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null); // { type, stationId, rows }

  const [noChangeOpen, setNoChangeOpen] = useState(false);
  const [noChangeStation, setNoChangeStation] = useState(null); // {id,name,outOfLimitItems}


const [plannedPrices, setPlannedPrices] = useState([]);
const [showPlannedPrices, setShowPlannedPrices] = useState(false);
const [pricesShop2000, setPricesShop2000] = useState(null);
  // EUR allowed fuels from backend
  const eurAllowedFuels = useMemo(
    () => new Set(layout?.eur_allowed_fuels || []),
    [layout?.eur_allowed_fuels]
  );

  // pro normalizaci NIKDY nepoužívej odvozené currencies (anyHasEUR)
  const currenciesForNormalize = layoutCurrencies;


  const formatCheckSource = (check) => {
  const source = check?.source_name || check?.note;

  const map = {
    tank_ono: "Tank ONO",
    shop2000: "SHOP2000",
    user_nearby_check: "Kontrola",
    admin_no_change: "Admin bez změn",
    admin_confirm: "Admin potvrzení",
    admin_no_change_confirm: "Admin bez změn",
    admin_confirm_changed_prices: "Admin potvrzení",
    manual_check: "Ruční kontrola",
  };

  return map[source] || source || "Kontrola";
};

const getCheckSourceClass = (check) => {
  const sourceType = check?.source_type;
  const sourceName = check?.source_name;

  if (sourceType === "automatic" || sourceName === "tank_ono" || sourceName === "shop2000") {
    return "status-info";
  }

  if (sourceType === "user" || sourceName === "user_nearby_check") {
    return "status-success";
  }

  if (sourceType === "admin") {
    return "status-warning";
  }

  return "";
};
  
  // normalize data
  useEffect(() => {
    if (!armexStationRaw?.id) return;

    const armexAllowed = allowedMap?.[armexStationRaw.id] || layoutFuels;

    const armexPrices = normalizeStationPrices({
      station: armexStationRaw,
      allowedFuels: armexAllowed,
      currencies: currenciesForNormalize,
    });

    const nearby = (nearbyRaw || []).map((s) => {
      const allowed = allowedMap?.[s.id] || layoutFuels;
      return {
        ...s,
        prices: normalizeStationPrices({
          station: s,
          allowedFuels: allowed,
          currencies: currenciesForNormalize,
        }),
      };
    });

    setArmexData({ ...armexStationRaw, prices: armexPrices });
    setStationData(nearby);
  }, [
    armexStationRaw?.id,
    nearbyRaw,
    allowedMap,
    layoutFuels,
    currenciesForNormalize,
    armexStationRaw,
  ]);

  useEffect(() => {
  if (!armexStationRaw?.id) return;

  let cancelled = false;

  const run = async () => {
    try {
      const [responsePlanned, responseShop] = await Promise.all([
        displayPlannedPrices({ station_id: armexStationRaw.id }),
        displayShop2000Prices({ station_id: armexStationRaw.id }),
      ]);

      if (cancelled) return;

      setPlannedPrices(transformPlannedPricesData(responsePlanned));
      setPricesShop2000(responseShop);
    } catch (e) {
      if (!cancelled) console.error("API call failed:", e);
    }
  };

  run();
  return () => { cancelled = true; };
}, [armexStationRaw?.id]);


  const showHeaderLabels = useShowHeaderLabels();

  // EDITING hook (IMPORTANT: pass eurAllowedFuels)
  const { changed, getInputValue, onBlurClearDraft, handleChange } = usePriceEditing({
    armexData,
    setArmexData,
    stationData,
    setStationData,
    eurAllowedFuels,
  });

  // ----- derived layout helpers -----
  const allStationsRaw = useMemo(
    () => [armexStationRaw, ...(nearbyRaw || [])].filter(Boolean),
    [armexStationRaw, nearbyRaw]
  );

  const usedFuels = useMemo(() => {
    const set = new Set();
    for (const st of allStationsRaw) {
      const allowed = allowedMap?.[st.id] || [];
      for (const f of allowed) set.add(f);
    }
    return set;
  }, [allStationsRaw, allowedMap]);

  const KURZ_GROUP = ["Kurz", "TF zboží", "TF cig"];

  const fuelColumns = useMemo(() => {
    const src = Array.isArray(layoutFuels) ? layoutFuels : [];
    const cols = [];
    let kurzInserted = false;

    for (const f of src) {
      if (KURZ_GROUP.includes(f)) {
        if (!kurzInserted && f === "Kurz") {
          cols.push({ key: "Kurz", label: "Kurz", fuels: KURZ_GROUP });
          kurzInserted = true;
        }
        continue;
      }
      cols.push({ key: f, label: f, fuels: [f] });
    }

    if (!kurzInserted && src.some((x) => KURZ_GROUP.includes(x))) {
      cols.push({ key: "Kurz", label: "Kurz", fuels: KURZ_GROUP });
    }
    return cols;
  }, [layoutFuels]);

  const visibleColumns = useMemo(() => {
    const cols = Array.isArray(layout?.columns) ? layout.columns : fuelColumns;
    return cols.filter((col) => (col.fuels || []).some((f) => usedFuels.has(f)));
  }, [layout?.columns, fuelColumns, usedFuels]);

  const hiddenInNearby = useMemo(
    () => new Set(layout?.hidden_in_nearby || []),
    [layout?.hidden_in_nearby]
  );

  const nearbyVisibleColumns = useMemo(() => {
    return visibleColumns.filter((col) => (col.fuels || []).every((f) => !hiddenInNearby.has(f)));
  }, [visibleColumns, hiddenInNearby]);

  // which fuels should render EUR "slot" anywhere (placeholder columns)
  const eurFuels = useMemo(() => {
    const set = new Set();

    // používáme RAW stanice (mají display_euro)
    for (const st of allStationsRaw) {
      if (!stationHasEUR(st)) continue;

      const allowed = allowedMap?.[st.id] || [];
      for (const fuel of allowed) {
        if (eurAllowedFuels.has(fuel)) set.add(fuel);
      }
    }

    set.delete("Kurz");
    set.delete("TF zboží");
    set.delete("TF cig");

    return set;
  }, [allStationsRaw, allowedMap, eurAllowedFuels]);

  const anyHasEUR = eurFuels.size > 0;
  const currencies = useMemo(
    () => (anyHasEUR ? layoutCurrencies : ["CZK"]),
    [anyHasEUR, layoutCurrencies]
  );

  // ----- validations / classes -----
  const hasLimitsForFuel = (station, fuel) =>
    Array.isArray(station?.limits) && station.limits.some((l) => l.price_type === fuel);

  const isPriceWithinLimits = (station, fuel, currency, value) => {
    const armexPrice = armexData?.prices?.[fuel]?.[currency]?.value;
    if (armexPrice === undefined || Number(value) <= 0) return false;

    const { min, max } = getLimitRange(station.limits, fuel);
    const diff = Number(value) - Number(armexPrice);
    return diff >= min && diff <= max;
  };

  const getInputClass = ({ station, fuel, currency, value, confirmed, isArmex }) => {
    if (value == null || value === "" || Number(value) === 0) return "";
    if (currency === "EUR" && !stationHasEUR(station)) return "";

    const isConfirmed = confirmed === true || confirmed === 1 || confirmed === "1";
    const k = fieldKey(station.id, fuel, currency);
    const isChanged = !!changed[k];

    if (!isConfirmed) return "input-changed";
    if (isChanged && isArmex) return "input-changed";

    if (!isArmex) {
      if (!hasLimitsForFuel(station, fuel) || fuel === "Kurz") return "input-default";
      const ok = isPriceWithinLimits(station, fuel, currency, value);
      return ok ? "input-default" : "input-lowest";
    }

    return "input-default";
  };

  const isExplicitlyUnconfirmed = (value) =>
    value === 0 || value === "0" || value === false;

  const userConfirmButton = useMemo(() => {
  if (!armexData || recentlyConfirmed) return false;

  for (const [, currs] of Object.entries(armexData.prices || {})) {
    for (const [, data] of Object.entries(currs || {})) {
      if (isExplicitlyUnconfirmed(data?.confirmed)) return true;
    }
  }

  return false;
}, [armexData, recentlyConfirmed]);


  const formatShortDateTime = (value, fallback = "Bez data") => {
  if (!value) return fallback;

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:[+-]\d{2}(?::?\d{2})?)?$/
  );

  if (!match) return raw;

  const [, year, month, day, hour, minute] = match;

  return `${day}.${month}. ${hour}:${minute}`;
};
  // ----- MODALS -----
  const openConfirm = ({ type, stationId = null }) => {
    if (!armexData) return;

    let targets = [];
    if (type === "ADMIN_CONFIRM" || type === "SCHEDULE" || type === "USER_CONFIRM_UNCONFIRMED") {
      const t = stationId === armexData.id ? armexData : stationData.find((s) => s.id === stationId);
      if (!t) return toast.error("Stanice nebyla nalezena");
      targets = [t];
    } else if (type === "USER_SUBMIT_ALL_NEARBY") {
      targets = stationData;
    }

    let items = [];
    if (type === "USER_CONFIRM_UNCONFIRMED") {
      items = buildUnconfirmedItemsForStation({ station: targets[0] });
    } else {
      items = targets.flatMap((st) => buildConfirmItemsForStation({ station: st, eurAllowedFuels }));
      if (type === "USER_SUBMIT_ALL_NEARBY") {
        items = enrichLimits(items, { armexData, stationData, isPriceWithinLimits });
      }
    }

    const warnings = [];
    const notSending = items.filter((i) => !i.willSend);
    const outOfLimits = items.filter((i) => i.limitStatus === "mimo limit");

    if (notSending.length) {
      warnings.push(
        `Některé položky se neodešlou (${notSending.length}) – neplatná hodnota / nepovolené EUR.`
      );
    }

    if (outOfLimits.length) {
      warnings.push(
        `Některé položky jsou mimo limity (${outOfLimits.length}) – budou čekat na potvrzení.`
      );
    }
    
    if (items.length === 0 && type !== "USER_SUBMIT_ALL_NEARBY") {
      warnings.push("Nejsou žádné změny k odeslání.");
    }

    if (items.length === 0 && type === "USER_SUBMIT_ALL_NEARBY") {
      warnings.push("Ceny budou potvrzeny jako zkontrolované beze změn.");
    }

    let rows = [];

    if (type === "ADMIN_CONFIRM") {
      const target = targets[0];
      rows = buildSubmitRowsForStation({
        station: target,
        userId: user.id,
        validFrom: null,
        mode: "admin",
        eurAllowedFuels,
      });

      if (rows.length === 0) {
        setNoChangeStation({
          id: target.id,
          name: target.name,
          outOfLimitItems: buildOutOfLimitNearbyItems({ stationData, armexData, eurAllowedFuels }),
        });
        setNoChangeOpen(true);
        return;
      }
    }

    if (type === "SCHEDULE") {
      if (!scheduledTime) return toast.warn("Vyberte Datum a čas");
      rows = buildSubmitRowsForStation({
        station: targets[0],
        userId: user.id,
        validFrom: scheduledTime,
        mode: "schedule",
        eurAllowedFuels,
      });
    }

    if (type === "USER_CONFIRM_UNCONFIRMED") {
      warnings.push("Potvrzením označíš nejnovější ceny pro tuto stanici jako potvrzené.");
      rows = [{ station_id: targets[0].id }];
    }

    if (type === "USER_SUBMIT_ALL_NEARBY") {
    const collect = (st) => {
      const base = buildSubmitRowsForStation({
        station: st,
        userId: user.id,
        validFrom: null,
        mode: "user",
        eurAllowedFuels,
        armexStationId: armexData.id,
        includeUnchanged: true,
      });

      return base.map((r) => ({
        ...r,
        confirmed: isPriceWithinLimits(st, r.fuel_type, r.currency, r.value) ? 1 : 0,
      }));
    };

    rows = stationData.flatMap(collect);
  }

    const mapTitle = {
      ADMIN_CONFIRM: "Potvrdit ceny — rekapitulace",
      SCHEDULE: "Naplánovat ceny — rekapitulace",
      USER_SUBMIT_ALL_NEARBY: "Potvrdit ceny okolních ČS — rekapitulace",
      USER_CONFIRM_UNCONFIRMED: "Potvrdit provedené změny — rekapitulace",
    };

    const subtitle =
      type === "SCHEDULE"
        ? `Ceny budou nastaveny od: ${scheduledTime}`
        : type === "ADMIN_CONFIRM"
          ? "Změny se uloží a budou považovány za potvrzené."
          : type === "USER_SUBMIT_ALL_NEARBY"
            ? "Změny mimo limity budou označeny jako čekající na potvrzení."
            : "";

    setConfirmTitle(mapTitle[type] || "Rekapitulace změn");
    setConfirmSubtitle(subtitle);
    setConfirmItems(items);
    setConfirmWarnings(warnings);
    setConfirmAction({ type, stationId, rows });
    setConfirmOpen(true);
  };

  const executeConfirmed = async () => {
    if (!confirmAction) return;
    const { type, rows } = confirmAction;

    try {
      if (type === "ADMIN_CONFIRM") {
        await submitPricesAdmin({ prices: rows });
        toast.success("Ceny změněny!");
        setArmexData((prev) => clearDirtyForStation(prev));
        await onPricesSaved?.();
      }

      if (type === "SCHEDULE") {
        if (!rows?.length) return toast.warn("Proveďte minimálně 1 změnu");
        await submitSchedulePriceChange({ prices: rows });
        toast.success(`Ceny nastaveny na ${scheduledTime}`);
        setArmexData((prev) => clearDirtyForStation(prev));
        setScheduledTime("");
        await onPricesSaved?.();
      }

      if (type === "USER_SUBMIT_ALL_NEARBY") {
        if (!rows?.length) {
          toast.warn("Nejsou dostupné žádné platné ceny okolních ČS ke kontrole.");
          return;
        }

        const res = await submitPrices({ prices: rows });

        if ((res?.changed_count ?? 0) > 0) {
          toast.success(
            `Ceny uloženy. Změn: ${res.changed_count}, beze změny: ${res.unchanged_count ?? 0}`
          );
        } else {
          toast.success("Okolní ČS zkontrolovány beze změn.");
        }

        setStationData((prev) => prev.map(clearDirtyForStation));
        await onPricesSaved?.();
      }

      if (type === "USER_CONFIRM_UNCONFIRMED") {
        await submitPricesUser({ station_id: armexData.id });
        toast.success("Ceny potvrzeny!");
        setRecentlyConfirmed(true);
        await onPricesSaved?.();
      }
    } catch (e) {
      console.error(e);
      toast.error("Odeslání selhalo.");
    } finally {
      setConfirmOpen(false);
      setConfirmAction(null);
    }
  };

  const executeNoChangeConfirm = async () => {
    if (!noChangeStation?.id) return;
    try {
      await submitPricesAdminWithoutChange({ stationID: noChangeStation.id });
      toast.success("Ceny potvrzeny (bez změn)");
      await onPricesSaved?.();
    } catch (e) {
      console.error(e);
      toast.error("Odeslání selhalo.");
    } finally {
      setNoChangeOpen(false);
      setNoChangeStation(null);
    }
  };

  // ----- SNAPSHOT -----
  const computeEurFuelsForStations = ({ stations, allowedMap, eurAllowedFuels }) => {
    const set = new Set();
    for (const st of stations) {
      if (!st) continue;
      if (!stationHasEUR(st)) continue;
      const allowed = allowedMap?.[st.id] || [];
      for (const fuel of allowed) {
        if (eurAllowedFuels?.has(fuel)) set.add(fuel);
      }
    }
    set.delete("Kurz");
    set.delete("TF zboží");
    set.delete("TF cig");
    return set;
  };
    
  const loadSnapshot = async (atIso) => {
    if (!armexData?.id) return;

    setSnapLoading(true);
    try {
      const res = await displayHistory({ station_id: armexData.id, at: atIso });

      const snapArmexRaw = res?.armex;
      const snapNearbyRaw = res?.nearby || [];

      const snapAllowedMap = res?.meta?.allowed_by_station || allowedMap;
      const snapLayoutFuels = res?.meta?.layout?.fuels || layoutFuels;
      const snapLayoutCurrencies = res?.meta?.layout?.currencies || layoutCurrencies;

      const armexAllowed = snapAllowedMap?.[snapArmexRaw?.id] || snapLayoutFuels;

      const snapArmex = {
        ...snapArmexRaw,
        prices: normalizeStationPrices({
          station: snapArmexRaw,
          allowedFuels: armexAllowed,
          currencies: snapLayoutCurrencies,
        }),
      };

      const snapNearby = snapNearbyRaw.map((s) => {
        const allowed = snapAllowedMap?.[s.id] || snapLayoutFuels;
        return {
          ...s,
          prices: normalizeStationPrices({
            station: s,
            allowedFuels: allowed,
            currencies: snapLayoutCurrencies,
          }),
        };
      });

      const eurFuelsSnapshot = computeEurFuelsForStations({
        stations: [snapArmexRaw, ...snapNearbyRaw],
        allowedMap: snapAllowedMap,
        eurAllowedFuels,
      });

      setSnapshot({
        atIso: res?.atIso ?? atIso,
        armex: snapArmex,
        nearby: snapNearby,
        meta: {
          allowed_by_station: snapAllowedMap,
          layout: {
            fuels: snapLayoutFuels,
            currencies: snapLayoutCurrencies,
            eurFuels: eurFuelsSnapshot,
          },
        },
      });
    } finally {
      setSnapLoading(false);
    }
  };

  const openSnapshot = async () => {
    setSnapOpen(true);
    setSummaryLoading(true);
    try {
      const res = await getHistoryChangesSummary({ station_id: armexData.id, months: 3 });
      setHistorySummary(res?.data ?? res);
    } finally {
      setSummaryLoading(false);
    }
  };

 if (!armexData) return null;

return (
  <section className="price-workspace">

    <div className="price-legend-card">
      <div className="legend-chip">
        <span className="legend-dot legend-default" />
        <span>V limitu</span>
      </div>

      <div className="legend-chip">
        <span className="legend-dot legend-outside" />
        <span>Potvrzeno mimo limit</span>
      </div>

      <div className="legend-chip">
        <span className="legend-dot legend-changed" />
        <span>Nepotvrzená změna</span>
      </div>
    </div>

  


    <div className="price-table-shell">
      {pricesShop2000 && (
        <Shop2000PricesSection
          pricesShop2000={pricesShop2000}
          armexData={armexData}
          compact
        />
      )}

      <PriceTableHeader
        show={showHeaderLabels}
        columns={visibleColumns}
        eurFuels={eurFuels}
      />

      <article className="price-card price-card-primary">
        <div className="price-card-header">
          <div>
            <div className="eyebrow">Armex ČS</div>
            <h3>{armexData.name}</h3>
           
          </div>
         
          <div className="station-status-list">
            {userConfirmButton && (
              <span className="status-pill status-danger">
                Nepotvrzené změny
              </span>
            )}
            {scheduledTime && (
              <span className="status-pill status-warning">
                Plánováno
              </span>
            )}
            {canPlannedView  && (
            <button
                type="button"
                className="btn btn-compact btn-muted"
                onClick={openSnapshot}
              >
                Stav k datu
              </button>
            )}
          </div>
        </div>

        <div className="station-row-wrap">
          <StationRow
            station={{ ...armexData, name: "" }}
            columns={visibleColumns}
            currencies={currencies}
            isArmex
            showHeaderLabels={showHeaderLabels}
            stationIndex={0}
            canEdit={canArmexEdit}
            getInputValue={getInputValue}
            onBlurClearDraft={onBlurClearDraft}
            onChangeValue={handleChange}
            getInputClass={getInputClass}
            eurAllowedFuels={eurAllowedFuels}
            eurFuels={eurFuels}
            allowedFuelsForStation={allowedMap?.[armexData.id] || layoutFuels}

          />
        </div>

        <div className="price-action-bar">
          <div className="price-action-left">
            {canArmexSchedule && (
              <input
                className="schedule-input"
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            )}
          </div>

          <div className="price-action-right">
            {canArmexConfirm && (
             <button
                className="btn btn-compact btn-primary-soft"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openConfirm({
                    type: "ADMIN_CONFIRM",
                    stationId: armexData.id,
                  });
                }}
              >
                Potvrdit ceny
              </button>
            )}

            {canArmexSchedule && (
               <button
                  type="button"
                  className="btn btn-compact btn-muted"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openConfirm({ type: "SCHEDULE", stationId: armexData.id });
                  }}
                >
                  Nastavit k datu
                </button>
            )}
          </div>
        </div>

        {canUserConfirm && userConfirmButton && (
          <div className="important-confirm-box">
            <div>
              <strong>Potvrzení provedení změn</strong>
              <p>
                Tato stanice obsahuje nepotvrzené ceny. Potvrďte je až po fyzickém provedení změn.
              </p>
            </div>

            <button
              className="btn btn-danger"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openConfirm({
                  type: "USER_CONFIRM_UNCONFIRMED",
                  stationId: armexData.id,
                });
              }}
            >
              Potvrzuji provedení
            </button>
          </div>
        )}
      </article>

     {canPlannedView && plannedPrices.length > 0 && (
            <PlannedPricesSection
              plannedPrices={plannedPrices}
              setPlannedPrices={setPlannedPrices}
              showPlannedPrices={showPlannedPrices}
              setShowPlannedPrices={setShowPlannedPrices}
              canPlannedDelete={canPlannedDelete}
              fuelOrderFromBackend={layoutFuels}
            />
          )}


      <form
        className="price-card nearby-card"
        onSubmit={(e) => {
          e.preventDefault();
          openConfirm({ type: "USER_SUBMIT_ALL_NEARBY" });
        }}
      >
        <div className="price-card-header nearby-card-header">
  <div>
    <h3>Okolní ČS</h3>
    <p>
      Zadejte ceny okolních ČS. Hodnoty mimo limit budou čekat na potvrzení.
    </p>
  </div>

        {(latestCompetitorCheck || lastCompetitorChecks.length > 0) && (
          <div className="nearby-check-summary">
            {latestCompetitorCheck ? (
              <div className="nearby-check-current">
                <span className={`nearby-check-source ${getCheckSourceClass(latestCompetitorCheck)}`}>
                  {formatCheckSource(latestCompetitorCheck)}
                </span>

                <span className="nearby-check-date">
                  {formatShortDateTime(latestCompetitorCheck.checked_at)}
                </span>

                <span className="nearby-check-count">
                  změny {latestCompetitorCheck.changed_count ?? 0}
                </span>
              </div>
            ) : (
              <div className="nearby-check-current nearby-check-current--empty">
                Bez kontroly
              </div>
            )}

            {lastCompetitorChecks.length > 0 && (
              <button
                type="button"
                className="nearby-check-toggle"
                onClick={() => setShowCheckHistory((prev) => !prev)}
              >
                Historie {showCheckHistory ? "▲" : "▼"}
              </button>
            )}
          </div>
        )}
      </div>
        {showCheckHistory && lastCompetitorChecks.length > 0 && (
        <div className="nearby-check-history">
          {lastCompetitorChecks.slice(0, 5).map((check) => (
            <div className="nearby-check-history-item" key={check.id}>
              <div className="nearby-check-history-main">
                <span className={`nearby-check-source ${getCheckSourceClass(check)}`}>
                  {formatCheckSource(check)}
                </span>

                <span>{formatShortDateTime(check.checked_at)}</span>

                {check.checked_by_username && (
                  <span>{check.checked_by_username}</span>
                )}
              </div>

              <small>
                změny {check.changed_count ?? 0} / beze změny{" "}
                {check.unchanged_count ?? 0} / celkem{" "}
                {check.total_checked_count ?? 0}
              </small>
            </div>
          ))}
        </div>
      )}
        <div className="nearby-list">
          {stationData.length === 0 ? (
            <div className="empty-inline">
              K této stanici nejsou přiřazeny žádné okolní ČS.
            </div>
          ) : (
            stationData.map((s, idx) => (
              <div className="nearby-row-shell" key={s.id}>
                <StationRow
                  station={s}
                  columns={nearbyVisibleColumns}
                  currencies={currencies}
                  isArmex={false}
                  showHeaderLabels={showHeaderLabels}
                  stationIndex={idx}
                  canEdit={canNearbyEdit}
                  getInputValue={getInputValue}
                  onBlurClearDraft={onBlurClearDraft}
                  onChangeValue={handleChange}
                  getInputClass={getInputClass}
                  eurAllowedFuels={eurAllowedFuels}
                  eurFuels={eurFuels}
                  allowedFuelsForStation={allowedMap?.[s.id] || layoutFuels}
                  showLatestPriceChange
                />
              </div>
            ))
          )}
        </div>

        {canNearbyEdit && stationData.length > 0 && (
          <div className="nearby-footer-actions">
            <button className="btn btn-compact btn-primary-soft" type="submit">
              Potvrdit ceny okolních ČS
            </button>
          </div>
        )}
      </form>
    </div>

    <PriceSnapshotDrawer
      open={snapOpen}
      onClose={() => setSnapOpen(false)}
      armexNow={armexData}
      nearbyNow={stationData}
      snapshot={snapshot}
      onLoadSnapshot={loadSnapshot}
      snapAt={snapAt}
      setSnapAt={setSnapAt}
      loading={snapLoading}
      compareNow={snapCompareNow}
      setCompareNow={setSnapCompareNow}
      eurAllowedFuels={eurAllowedFuels}
      eurFuelsNow={eurFuels}
      eurFuelsSnapshot={snapshot?.meta?.layout?.eurFuels}
      historySummary={historySummary}
      summaryLoading={summaryLoading}
      fuelOrder={layoutFuels}
    />

    <ConfirmChangesModal
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      onConfirm={executeConfirmed}
      title={confirmTitle}
      subtitle={confirmSubtitle}
      items={confirmItems}
      warnings={confirmWarnings}
      allowEmptyConfirm={confirmAction?.type === "USER_SUBMIT_ALL_NEARBY"}
      confirmLabel={
        confirmAction?.type === "USER_SUBMIT_ALL_NEARBY" && confirmItems.length === 0
          ? "Potvrdit kontrolu"
          : "Potvrdit a odeslat"
      }
    />

    <ConfirmNoChangeModal
      open={noChangeOpen}
      onClose={() => {
        setNoChangeOpen(false);
        setNoChangeStation(null);
      }}
      onConfirm={executeNoChangeConfirm}
      stationName={noChangeStation?.name}
      stationId={noChangeStation?.id}
      items={noChangeStation?.outOfLimitItems || []}
    />
  </section>
);
}

function useShowHeaderLabels() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const checkWidth = () => setShow(window.innerWidth > 1100);
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  return show;
}
